import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useGlobal } from "../../contexts/GlobalContext";
import { isAdminRole } from "../../utils/roles";
import request from "../../utils/request";
import { MCPServerInfo } from "../../types/MCPTypes";

const { Paragraph, Text } = Typography;

interface ApiResponse<T> {
  code: number;
  msg: string;
  data: T;
}

interface MarketplaceResponse {
  total: number;
  list: MCPServerInfo[];
}

const statusColor: Record<string, string> = {
  private: "default",
  pending: "processing",
  listed: "success",
  rejected: "error",
  unlisted: "warning",
};

const statusText: Record<string, string> = {
  private: "私有",
  pending: "待审核",
  listed: "已上架",
  rejected: "已拒绝",
  unlisted: "已下架",
};

const MCPMarketplaceTab: React.FC = () => {
  const { API_BASE_URL, user } = useGlobal();
  const isAdmin = isAdminRole(user?.apiInfo?.role);
  const [publicLoading, setPublicLoading] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [serversLoading, setServersLoading] = useState(false);
  const [publicItems, setPublicItems] = useState<MCPServerInfo[]>([]);
  const [adminItems, setAdminItems] = useState<MCPServerInfo[]>([]);
  const [ownServers, setOwnServers] = useState<MCPServerInfo[]>([]);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [form] = Form.useForm();

  const fetchMarketplace = useCallback(async () => {
    setPublicLoading(true);
    try {
      const res = await request.get<ApiResponse<MarketplaceResponse>>(
        `${API_BASE_URL}/api/v3/mcp/marketplace`
      );
      setPublicItems(res.data.data?.list || []);
    } catch (error: any) {
      message.error(error.response?.data?.msg || "获取 MCP 广场失败");
    } finally {
      setPublicLoading(false);
    }
  }, [API_BASE_URL]);

  const fetchOwnServers = useCallback(async () => {
    setServersLoading(true);
    try {
      const res = await request.get<ApiResponse<MCPServerInfo[]>>(
        `${API_BASE_URL}/api/v3/mcp/servers`
      );
      setOwnServers(res.data.data || []);
    } catch (error: any) {
      message.error(error.response?.data?.msg || "获取 MCP 服务器失败");
    } finally {
      setServersLoading(false);
    }
  }, [API_BASE_URL]);

  const fetchAdminItems = useCallback(async () => {
    if (!isAdmin) return;
    setAdminLoading(true);
    try {
      const res = await request.get<ApiResponse<MarketplaceResponse>>(
        `${API_BASE_URL}/api/v3/admin/mcp/marketplace`
      );
      setAdminItems(res.data.data?.list || []);
    } catch (error: any) {
      message.error(error.response?.data?.msg || "获取审核记录失败");
    } finally {
      setAdminLoading(false);
    }
  }, [API_BASE_URL, isAdmin]);

  useEffect(() => {
    fetchMarketplace();
    fetchOwnServers();
    fetchAdminItems();
  }, [fetchMarketplace, fetchOwnServers, fetchAdminItems]);

  const publish = async () => {
    const values = await form.validateFields();
    setPublishing(true);
    try {
      await request.post(
        `${API_BASE_URL}/api/v3/mcp/servers/${values.serverId}/marketplace/publish`,
        { review_prompt: values.reviewPrompt || "" }
      );
      message.success("已提交 MCP 广场审核");
      setPublishOpen(false);
      form.resetFields();
      fetchMarketplace();
      fetchOwnServers();
      fetchAdminItems();
    } catch (error: any) {
      message.error(error.response?.data?.msg || "提交审核失败");
    } finally {
      setPublishing(false);
    }
  };

  const updateStatus = async (server: MCPServerInfo, status: string) => {
    try {
      await request.put(`${API_BASE_URL}/api/v3/admin/mcp/marketplace/${server.id}/status`, {
        status,
        review_prompt: server.review_prompt || "",
      });
      message.success("审核状态已更新");
      fetchMarketplace();
      fetchAdminItems();
    } catch (error: any) {
      message.error(error.response?.data?.msg || "更新审核状态失败");
    }
  };

  const recheck = async () => {
    setAdminLoading(true);
    try {
      const res = await request.post<ApiResponse<{ checked: number; unlisted: number }>>(
        `${API_BASE_URL}/api/v3/admin/mcp/marketplace/recheck`
      );
      message.success(`复查完成: ${res.data.data.checked} 个, 下架 ${res.data.data.unlisted} 个`);
      fetchMarketplace();
      fetchAdminItems();
    } catch (error: any) {
      message.error(error.response?.data?.msg || "复查失败");
    } finally {
      setAdminLoading(false);
    }
  };

  const publishOptions = useMemo(
    () =>
      ownServers.map((server) => ({
        value: server.id,
        label: `${server.name} · ${server.endpoint}`,
      })),
    [ownServers]
  );

  const publicColumns: ColumnsType<MCPServerInfo> = [
    {
      title: "名称",
      dataIndex: "name",
      render: (name, record) => (
        <Space direction="vertical" size={2}>
          <Text strong>{name}</Text>
          <Text type="secondary" copyable>
            {record.endpoint}
          </Text>
        </Space>
      ),
    },
    {
      title: "来源 / 风险",
      width: 210,
      render: (_, record) => (
        <Space wrap>
          <Tag>第三方</Tag>
          <Tag color={record.permissions_declared ? "blue" : "orange"}>
            {record.permissions_declared ? "已声明权限" : "权限未知"}
          </Tag>
          {record.side_effects_declared && <Tag color="red">含副作用能力</Tag>}
        </Space>
      ),
    },
    {
      title: "状态",
      width: 120,
      render: (_, record) => (
        <Tag color={statusColor[record.listing_status || "listed"]}>
          {statusText[record.listing_status || "listed"]}
        </Tag>
      ),
    },
    {
      title: "上架时间",
      width: 180,
      render: (_, record) => (record.listed_at ? new Date(record.listed_at).toLocaleString() : "-"),
    },
  ];

  const adminColumns: ColumnsType<MCPServerInfo> = [
    ...publicColumns,
    {
      title: "失败次数",
      width: 100,
      dataIndex: "consecutive_failures",
      render: (value) => value || 0,
    },
    {
      title: "审核结果",
      width: 240,
      render: (_, record) => (
        <Paragraph ellipsis={{ rows: 2 }} style={{ margin: 0 }}>
          {record.review_result?.reason ||
            record.review_result?.last_check_error ||
            record.review_prompt ||
            "-"}
        </Paragraph>
      ),
    },
    {
      title: "操作",
      width: 260,
      render: (_, record) => (
        <Space wrap>
          <Button size="small" onClick={() => updateStatus(record, "listed")}>
            上架
          </Button>
          <Button size="small" danger onClick={() => updateStatus(record, "rejected")}>
            拒绝
          </Button>
          <Button size="small" onClick={() => updateStatus(record, "unlisted")}>
            下架
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card
        title="MCP 广场"
        extra={
          <Space>
            <Button onClick={fetchMarketplace}>刷新</Button>
            <Button type="primary" loading={serversLoading} onClick={() => setPublishOpen(true)}>
              发布 MCP
            </Button>
          </Space>
        }
      >
        <Alert
          showIcon
          type="warning"
          message="广场中的 MCP 均为第三方服务"
          description="上架表示通过当前平台审核与健康检查，不代表官方背书。添加后默认不可执行；必须由后端逐工具配置权限、风险和调用限制，有副作用的工具还必须在执行时完成确认。"
          style={{ marginBottom: 16 }}
        />
        <Table
          rowKey="id"
          columns={publicColumns}
          dataSource={publicItems}
          loading={publicLoading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {isAdmin && (
        <Card
          title="审核与人工干预"
          extra={
            <Space>
              <Button onClick={fetchAdminItems}>刷新</Button>
              <Button loading={adminLoading} onClick={recheck}>
                执行复查
              </Button>
            </Space>
          }
        >
          <Table
            rowKey="id"
            columns={adminColumns}
            dataSource={adminItems}
            loading={adminLoading}
            pagination={{ pageSize: 10 }}
          />
        </Card>
      )}

      <Modal
        title="发布到 MCP 广场"
        open={publishOpen}
        onOk={publish}
        confirmLoading={publishing}
        onCancel={() => setPublishOpen(false)}
        okText="提交审核"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="serverId" label="MCP 服务器" rules={[{ required: true, message: "请选择 MCP 服务器" }]}>
            <Select
              loading={serversLoading}
              options={publishOptions}
              placeholder="选择要发布的 MCP 服务器"
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="reviewPrompt" label="审核提示词">
            <Input.TextArea rows={4} placeholder="补充用途、数据范围、安全约束或审核说明" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default MCPMarketplaceTab;
